import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Button, Flex, VStack, Text, Image, Link } from '@chakra-ui/react'
import { Trans, useTranslation } from 'react-i18next'
import { CheckboxConfirm } from './checkbox-confirm'
import { cardImageMap, ScenarioType } from '../../state/interfaces'
import { useSelector } from 'react-redux'
import { selectScenario } from '../../state'
import { getLocale } from '../../i18n/locale'
export interface CheckboxConfirmProps {
  onChange?: (newValues: string[]) => void
  onConfirm?: (values: string[]) => void
  grandImage?: string
}

export const CheckboxesConfirm: FC<CheckboxConfirmProps> = ({ onConfirm, grandImage }) => {
  const { t } = useTranslation()

  const [selectedChoices, setSelectedChoices] = useState<string[]>([])
  const [isConfirmedPrice, setIsConfirmedPrice] = useState(false)
  const [isAwardScenario, setIsAwardScenario] = useState(false)
  const scenario = useSelector(selectScenario)
  const scenarioType = scenario?.type
  const awardsLinkRules =
    getLocale() === 'fr' ? 'https://www.planhub.ca/fr/awards/reglement' : 'https://www.planhub.ca/awards/rules'
  const contestLinksRules =
    getLocale() === 'fr' ? 'https://www.planhub.ca/fr/awards/reglement' : 'https://www.planhub.ca/awards/rules'

  const handleCheckboxChange = (value: string, checked: boolean) => {
    if (checked) {
      setSelectedChoices((prevSelected) => [...prevSelected, value])
    } else {
      setSelectedChoices((prevSelected) => prevSelected.filter((val) => val !== value))
    }
  }
  const image = useMemo(() => (grandImage ? cardImageMap[grandImage] : ''), [grandImage])

  useEffect(() => {
    if (isAwardScenario) {
      setIsConfirmedPrice(selectedChoices.includes('confirm-alert') && selectedChoices.includes('confirm-newsletters'))
    } else {
      setIsConfirmedPrice(selectedChoices.includes('confirm-alert'))
    }
  }, [selectedChoices])

  const confirmChoices = useCallback(() => {
    onConfirm && onConfirm(selectedChoices)
  }, [onConfirm, selectedChoices])

  useEffect(() => {
    if (scenarioType === ScenarioType.MOBILE_REVIEWS_AWARD || scenarioType === ScenarioType.INTERNET_REVIEWS_AWARD) {
      setIsAwardScenario(true)
    }
  }, [scenarioType])

  return (
    <VStack>
      {isAwardScenario && (
        <Text marginBottom={4}>
          <strong>{t('please-read')}</strong>
          <Link color={'brand'} href={awardsLinkRules} target="_blank" rel="noopener noreferrer">
            <strong>{t('prix-planhub-2024-link-title')}</strong>
          </Link>
        </Text>
      )}
      <Flex direction="column" marginY={5} maxW={{ md: 600 }}>
        <label>
          <Box border="1px solid lightgrey" borderRadius={6}>
            <Box display="flex" width="100%" paddingX={6} paddingY={2}>
              <Flex alignItems="center" minW={{ base: 120, md: 200 }}>
                <Flex border="1px solid lightgrey" alignItems="center" mt={1} minW={'18px'} zIndex={2}>
                  <CheckboxConfirm value="confirm-alert" onChange={handleCheckboxChange} />
                </Flex>
                <Text ml={4} whiteSpace="normal" cursor="pointer" fontSize={{ base: '14', md: '16' }}>
                  <strong>
                    {t('mandatory-confirmation-title')}{' '}
                    <span style={{ color: 'red', textTransform: 'uppercase' }}>*</span>
                  </strong>
                  <br />
                  {scenarioType === ScenarioType.MOBILE_REVIEWS_AWARD ||
                  scenarioType === ScenarioType.INTERNET_REVIEWS_AWARD ? (
                    <span>
                      <span>{t('mandatory-confirmation-fragment-1')}</span>
                      <strong>{t('mandatory-confirmation-fragment-2')}</strong>
                      <Link color={'brand'} href={awardsLinkRules} target="_blank" rel="noopener noreferrer">
                        <strong>{t('mandatory-confirmation-fragment-3')}</strong>
                      </Link>
                    </span>
                  ) : scenarioType === 'mobile-reviews' || scenarioType === 'internet-reviews' ? (
                    t('mandatory-confirmation-text-no-email')
                  ) : (
                    t('mandatory-confirmation-text')
                  )}
                </Text>
              </Flex>
            </Box>
          </Box>
        </label>
        <label>
          <Box border="1px solid lightgrey" borderRadius={6} mt={4} backgroundColor={isConfirmedPrice ? '#F1FFE2' : ''}>
            <Box display="flex" width="100%" paddingX={6} paddingY={2}>
              <Flex alignItems="center" minW={{ base: 120, md: 200 }}>
                <Flex border="1px solid lightgrey" alignItems="center" mt={1} minW={'18px'} zIndex={2}>
                  <CheckboxConfirm value="confirm-contest" onChange={handleCheckboxChange} />
                </Flex>
                <Text ml={4} whiteSpace="normal" cursor="pointer" fontSize={{ base: '14', md: '16' }}>
                  <strong>{t('optional-confirmation-contest-title')}</strong>
                  <br />
                  <Trans
                    i18nKey="contest-2025-text"
                    components={{
                      strong: <strong style={{ fontWeight: 900 }} />,
                    }}
                  />
                </Text>
              </Flex>
            </Box>
          </Box>
        </label>
        <Link color={'brand'} href={contestLinksRules} target="_blank" rel="noopener noreferrer" textAlign={'end'}>
          <strong>{t('contest-2025-link-title')}</strong>
        </Link>
        <label>
          <Box
            backgroundColor={!isConfirmedPrice && !isAwardScenario ? '#a3a3a3' : ''}
            border="1px solid lightgrey"
            borderRadius={6}
            mt={4}
            opacity={isConfirmedPrice || isAwardScenario ? 1 : 0.8}
            pointerEvents={isConfirmedPrice || isAwardScenario ? 'auto' : 'none'}
          >
            <Box display="flex" width="100%" paddingX={6} paddingY={2}>
              <Flex alignItems="center" minW={{ base: 120, md: 200 }}>
                <Flex border="1px solid lightgrey" alignItems="center" mt={1} minW={'18px'} zIndex={2}>
                  <CheckboxConfirm
                    value="confirm-newsletters"
                    onChange={handleCheckboxChange}
                    isDisabled={!isConfirmedPrice && !isAwardScenario}
                    // isChecked={isAwardScenario}
                  />
                </Flex>
                <Text ml={4} whiteSpace="normal" cursor="pointer" fontSize={{ base: '14', md: '16' }}>
                  {isAwardScenario ? (
                    <strong>
                      {t('mandatory-confirmation-title')}{' '}
                      <span style={{ color: 'red', textTransform: 'uppercase' }}>*</span>
                    </strong>
                  ) : (
                    <strong>{t('optional-confirmation-title')}</strong>
                  )}
                  <br />
                  {t('optional-confirmation-text')}
                </Text>
              </Flex>
            </Box>
          </Box>
        </label>
      </Flex>
      <Button
        onClick={confirmChoices}
        disabled={!isConfirmedPrice}
        variant="solid"
        color="white"
        backgroundColor="green.600"
        fontSize="xs"
        fontWeight="bold"
        borderWidth="2px"
        borderColor="green.600"
        px={16}
        py={6}
        _focus={{
          borderColor: 'brand',
          boxShadow: '0 0 0 2px #5bce4c',
        }}
      >
        {t('common_continue')}
      </Button>
      {image && <Image src={image} alt="image" width={['60%', '45%']} />}
    </VStack>
  )
}
